import { pluralize, timeout } from '@art-suite/art-core-ts';

import { DEFAULT_BACKOFF_FACTOR, DEFAULT_MAX_RETRY_DELAY_MS, DEFAULT_RETRY_DELAY_MS, getRetryDelay } from '@kaiizen/react-persistent-cache';
import { ErrorWithStatus } from '@kaiizen/zod-less-api';

import { ServerReachabilityStatus } from '../../useServerReachabilityCheck';
import { SyncRecordBase } from '../persistence';
import { SyncCompletionStats, Syncer } from './syncItemBatch';
import { isHardFailure } from './syncLib';

const MAX_RETRY_COUNT = 20;
const BAD_CONNECTION_MIN_DELAY_MS = 1_000;
const BAD_CONNECTION_MAX_DELAY_MS = 10_000;

/**
 * Attempt "sync" repeatedly until there are no more pending items or we exceed the max retry count.
 *
 * 1. Resets stale `isSyncing` flags via `model.resetSyncStatus()`
 * 2. Fetches pending records via `model.getPending()`
 * 3. Checks server reachability before syncing
 * 4. Passes records to `sync()`
 * 5. On success (count > 0): resets retry state, loops again
 * 6. On zero progress (count === 0 with pending items): backs off (prevents hot loop)
 * 7. On error: classifies, logs, backs off, loops again
 * 8. Exits after MAX_RETRY_COUNT consecutive errors or when nothing is pending
 */
export const syncItemsUntilDone = async <T extends SyncRecordBase>(
  { model, sync, addError, syncLogger, onComplete, isPausedRef }: Syncer<T>,
  checkServerReachability: () => Promise<ServerReachabilityStatus>,
) => {
  const type = model.getType();
  let retryCount = 0;
  let totalSynced = 0;

  while (true) {
    if (isPausedRef.current) {
      syncLogger('info', type, 'Sync paused — exiting sync loop.');
      break;
    }
    let syncedThisIteration = 0;
    let hadError = false;

    try {
      await model.resetSyncStatus();
      const pending = await model.getPending();
      if (pending.length === 0) {
        if (totalSynced > 0) syncLogger('info', type, `Done. Synced ${pluralize(totalSynced, 'item')}.`);
        break;
      }

      // ****************************************************************
      // Server Reachability Check
      // ****************************************************************
      const reachability = await checkServerReachability();
      if (!reachability.reachable) {
        syncLogger('info', type, `Server unreachable (${reachability.responseTimeMs}ms). Backing off.`);
        hadError = true;
      } else {
        if (reachability.quality === 'bad') {
          syncLogger('info', type, `Bad connection (${reachability.responseTimeMs}ms).`);
        }

        // ****************************************************************
        // Main Sync Action
        // ****************************************************************
        syncLogger('info', type, `Starting. ${pluralize(pending.length, 'pending item')}.`);
        syncedThisIteration = await sync(pending);
        totalSynced += syncedThisIteration;

        if (syncedThisIteration > 0) {
          syncLogger('info', type, `Synced ${pluralize(syncedThisIteration, 'item')}.`);
        } else {
          // All pending items failed individually (caught inside syncItemBatch).
          // Treat as a soft error to prevent hot-looping.
          syncLogger('warn', type, `No items synced out of ${pluralize(pending.length, 'pending item')}. Backing off.`);
          hadError = true;
        }
      }
    } catch (error) {
      const errorInstance = error instanceof Error ? error : new Error(String(error));
      const hardError = !(error instanceof ErrorWithStatus) || isHardFailure(error);
      syncLogger(hardError ? 'error' : 'warn', type, 'Sync error:', error);
      addError(hardError, `sync worker ${type}`, errorInstance);
      hadError = true;
    }

    // Backoff / exit logic
    if (hadError) {
      retryCount++;
      if (retryCount > MAX_RETRY_COUNT) {
        syncLogger('error', type, `Exceeded max retry count (${MAX_RETRY_COUNT}). Exiting sync loop.`);
        break;
      }
      const delay = getRetryDelay(DEFAULT_RETRY_DELAY_MS, DEFAULT_BACKOFF_FACTOR, retryCount, DEFAULT_MAX_RETRY_DELAY_MS);
      syncLogger('debug', type, `Retrying in ${delay}ms (retry ${retryCount}/${MAX_RETRY_COUNT}).`);
      await timeout(delay);
    } else if (syncedThisIteration > 0) {
      retryCount = 0;
    }
  }

  if (onComplete) {
    await model.resetSyncStatus();
    const remaining = await model.getPending();
    const stats: SyncCompletionStats = { synced: totalSynced, unsynced: remaining.length, total: totalSynced + remaining.length };
    try {
      await onComplete(stats);
    } catch (error) {
      syncLogger('warn', type, 'onComplete failed:', error);
    }
  }
};
